import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Card,
} from "@mui/material";
import { Add, Delete, Save } from "@mui/icons-material";
import { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import QuestionWrapper from "./components/QuestionWrapper";
import LoadingScreen from "components/Loading";
import MDButton from "components/MDButton";
import { saveJobForm } from "services/api";
import { saveJobFormQuestions } from "services/api";
import SnackbarWrapper from "components/Snackbar";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/authContext";
import { getTenantIdFromKey } from "services/utils";

const FormCreationPage = ({ form, setForm, formQuestions, setFormQuestions, canEdit, status }) => {
  const { t } = useTranslation();
  const [editingTitle, setEditingTitle] = useState(false);
  const { companies, employee } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const snackbarRef = useRef();

  const questionTypes = [
    { type: "SHORT_TEXT" },
    { type: "LONG_TEXT" },
    { type: "RADIO" },
    { type: "CHECKBOX" },
  ];

  const addQuestion = (type) => {
    const newQuestion = {
      formId: form?.id,
      questionText: "",
      fieldType: type,
      isRequired: false,
      options: "", // e.g. "Option1;Option2"
      orderIndex: formQuestions.length,
    };
    setFormQuestions([...formQuestions, newQuestion]);
    setAnchorEl(null);
  };

  const deleteQuestion = (index) => {
    const arr = [...formQuestions];
    arr.splice(index, 1);
    // Reassign orderIndex
    setFormQuestions(arr.map((q, idx) => ({ ...q, orderIndex: idx })));
  };

  const saveForm = async () => {
    const res = await saveJobFormQuestions(formQuestions);
    if (res != "error") {
      if (res && Array.isArray(res)) {
        // 3. UPDATE STATE with the backend data
        // This ensures your questions now have IDs for future updates
        setFormQuestions(res);

        snackbarRef.current?.showSnackbar(t("admin.formCreation.messages.formSaved"), "success");
      } else {
        // Handle case where backend didn't return data
        console.error("Backend did not return saved questions");
      }
    }
  };

  const renderedQuestions = useMemo(
    () =>
      formQuestions?.map((q, index) => (
        <Draggable key={index} draggableId={String(index)} index={index} isDragDisabled={!canEdit}>
          {(providedDrag) => (
            <Box
              ref={providedDrag.innerRef}
              {...providedDrag.draggableProps}
              {...providedDrag.dragHandleProps}
            >
              <Card sx={{ p: 2 }}>
                <Box sx={{ position: "relative" }}>
                  {/* Question component */}
                  <QuestionWrapper
                    question={q}
                    updateQuestion={(updated) => {
                      const arr = [...formQuestions];
                      arr[index] = updated;
                      setFormQuestions(arr);
                    }}
                    canEdit={canEdit}
                  />

                  {/* Delete Button */}
                  {canEdit && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteQuestion(index)}
                      sx={{
                        position: "absolute",
                        bottom: 5,
                        right: 5,
                        zIndex: 1, // make sure it's above the input
                        backgroundColor: "background.paper", // optional, to cover input underneath
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </Card>
            </Box>
          )}
        </Draggable>
      )),
    [formQuestions]
  );

  if (!form || !formQuestions) {
    return <LoadingScreen />;
  }
  return (
    <Box p={3} maxWidth={800} mx="auto">
      {/* --- Form Title --- */}
      <Box mb={3} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* TITLE AREA → takes 80% */}
        <Box sx={{ flex: 0.8 }}>
          {canEdit && (editingTitle || !form.title || form.title.trim() === "") ? (
            <TextField
              fullWidth
              autoFocus
              value={form.title || ""}
              label={t("admin.formCreation.formTitle")}
              onBlur={async () => {
                const newform = await saveJobForm(form);
                if (newform != "error") {
                  snackbarRef.current?.showSnackbar(
                    t("admin.formCreation.messages.formSaved"),
                    "success"
                  );
                }

                if (!form.title || form.title.trim() === "") return;

                setEditingTitle(false);
              }}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          ) : (
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ cursor: canEdit ? "pointer" : "default" }}
              onClick={() => {
                if (canEdit) setEditingTitle(true);
              }}
            >
              {t("admin.applicants.app_form")}
            </Typography>
          )}
        </Box>

        {/* BUTTON → takes 20% */}
        <Box sx={{ flex: 0.2, display: "flex", justifyContent: "flex-end" }}>
          {status != "COMPLETED" && status != "REJECTED" && (
            <MDButton
              color="primary"
              variant="text"
              onClick={() => {
                const tenantKey = localStorage.getItem("tenant");
                const link = `${window.location.origin}/forms/${tenantKey}/${form.publicId}/${employee.id}`;
                navigator.clipboard.writeText(link);
                snackbarRef.current?.showSnackbar(
                  t("admin.formCreation.messages.linkCopied"),
                  "success"
                );
              }}
            >
              {t("admin.formCreation.buttons.copyLink")}
            </MDButton>
          )}
        </Box>
      </Box>

      {/* --- Questions List with Drag-and-Drop --- */}
      {/* Note: You might also want to pass 'isDragDisabled={!canEdit}' to your Draggable items inside renderedQuestions if possible */}
      <DragDropContext
        onDragEnd={(result) => {
          if (!canEdit) return; // Prevent reordering logic if read-only
          if (!result.destination) return;
          const items = Array.from(formQuestions);
          const [reordered] = items.splice(result.source.index, 1);
          items.splice(result.destination.index, 0, reordered);
          // Update orderIndex too
          setFormQuestions(items.map((q, idx) => ({ ...q, orderIndex: idx })));
        }}
      >
        <Droppable droppableId="questions">
          {(provided) => (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {renderedQuestions}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {/* --- Add Question Button (Only visible if canEdit) --- */}
      {canEdit && (
        <Box mt={3} textAlign="center">
          <MDButton
            color="primary"
            variant="contained"
            startIcon={<Add />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            {t("admin.formCreation.buttons.addQuestion")}
          </MDButton>
          <MDButton
            color="primary"
            variant="contained"
            startIcon={<Save />}
            onClick={saveForm}
            sx={{ marginLeft: 1 }}
          >
            {t("admin.formCreation.buttons.saveQuestions")}
          </MDButton>

          <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
            {questionTypes.map((q) => (
              <MenuItem key={q.type} onClick={() => addQuestion(q.type)}>
                {/* {q.label} */}
                {t(`admin.formCreation.questionTypes.${q.type}`)}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}
      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </Box>
  );
};

FormCreationPage.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  formQuestions: PropTypes.array.isRequired,
  setFormQuestions: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  status: PropTypes.string,
};

export default FormCreationPage;
